// Filter registry — maps filter id strings to Filter instances.
// Used by Smart Filter rendering and the ApplySmartFilterCommand to
// resolve filter implementations by their stable identifier.

import type { Filter } from "./Filter";
import { GaussianBlur } from "./GaussianBlur";
import { BoxBlur } from "./BoxBlur";
import { MotionBlur } from "./MotionBlur";
import { SharpenSuite } from "./SharpenSuite";
import { NoiseSuite } from "./NoiseSuite";
import { PixelateSuite } from "./PixelateSuite";
import { DistortSuite } from "./DistortSuite";
import { StylizeSuite } from "./StylizeSuite";
import { UtilitySuite } from "./UtilitySuite";
import { AverageBlur } from "./AverageBlur";
import { SurfaceBlur } from "./SurfaceBlur";
import { SmartBlur } from "./SmartBlur";
import { LensBlur } from "./LensBlur";
import { RadialBlur } from "./RadialBlur";
import { ShapeBlur } from "./ShapeBlur";
import { FieldBlur } from "./FieldBlur";
import { IrisBlur } from "./IrisBlur";
import { TiltShift } from "./TiltShift";
import { Displace } from "./Displace";
import { Ripple } from "./Ripple";
import { Shear } from "./Shear";
import { Wave } from "./Wave";
import { ZigZag } from "./ZigZag";
import { PolarCoordinates } from "./PolarCoordinates";
import { Crystallize } from "./Crystallize";
import { Facet } from "./Facet";
import { Fragment } from "./Fragment";
import { Mezzotint } from "./Mezzotint";
import { Pointillize } from "./Pointillize";
import { Diffuse } from "./Diffuse";
import { Extrude } from "./Extrude";
import { Solarize } from "./Solarize";
import { Tiles } from "./Tiles";
import { TraceContour } from "./TraceContour";
import { Wind } from "./Wind";
import { GlowingEdges } from "./GlowingEdges";
import { Fibers } from "./Fibers";
import { LensFlare } from "./LensFlare";
import { LightingEffects } from "./LightingEffects";
import { PictureFrame } from "./PictureFrame";
import { OilPaint } from "./OilPaint";
import { SmartSharpen } from "./SmartSharpen";
import { HDRToning } from "./HDRToning";
import { MatchColor } from "./MatchColor";
import { ReplaceColor } from "./ReplaceColor";
import { Equalize } from "./Equalize";
import { CameraRaw } from "./CameraRaw";
import { BlurGallery } from "./BlurGallery";
import { SharpenEdges } from "./SharpenEdges";
import { CustomKernel } from "./CustomKernel";
import { Liquify } from "./Liquify";
import { Invert } from "./Invert";

const registry = new Map<string, Filter<unknown>>();

function register(filter: Filter<unknown>): void {
  registry.set(filter.id, filter);
}

// Register individual filters
register(GaussianBlur as Filter<unknown>);
register(BoxBlur as Filter<unknown>);
register(MotionBlur as Filter<unknown>);
register(AverageBlur as Filter<unknown>);
register(SurfaceBlur as Filter<unknown>);
register(SmartBlur as Filter<unknown>);
register(LensBlur as Filter<unknown>);
register(RadialBlur as Filter<unknown>);
register(ShapeBlur as Filter<unknown>);
register(FieldBlur as Filter<unknown>);
register(IrisBlur as Filter<unknown>);
register(TiltShift as Filter<unknown>);
register(Displace as Filter<unknown>);
register(Ripple as Filter<unknown>);
register(Shear as Filter<unknown>);
register(Wave as Filter<unknown>);
register(ZigZag as Filter<unknown>);
register(PolarCoordinates as Filter<unknown>);
register(Crystallize as Filter<unknown>);
register(Facet as Filter<unknown>);
register(Fragment as Filter<unknown>);
register(Mezzotint as Filter<unknown>);
register(Pointillize as Filter<unknown>);
register(Diffuse as Filter<unknown>);
register(Extrude as Filter<unknown>);
register(Solarize as Filter<unknown>);
register(Tiles as Filter<unknown>);
register(TraceContour as Filter<unknown>);
register(Wind as Filter<unknown>);
register(GlowingEdges as Filter<unknown>);
register(Fibers as Filter<unknown>);
register(LensFlare as Filter<unknown>);
register(LightingEffects as Filter<unknown>);
register(PictureFrame as Filter<unknown>);
register(new OilPaint() as unknown as Filter<unknown>);
register(new SmartSharpen() as unknown as Filter<unknown>);

// Register suite filters (each suite is a single Filter with mode param)
register(SharpenSuite as Filter<unknown>);
register(NoiseSuite as Filter<unknown>);
register(PixelateSuite as Filter<unknown>);
register(DistortSuite as Filter<unknown>);
register(StylizeSuite as Filter<unknown>);
register(UtilitySuite as Filter<unknown>);

// Register new filters
register(HDRToning as Filter<unknown>);
register(MatchColor as Filter<unknown>);
register(ReplaceColor as Filter<unknown>);
register(Equalize as Filter<unknown>);
register(CameraRaw as Filter<unknown>);
register(BlurGallery as Filter<unknown>);
register(SharpenEdges as Filter<unknown>);
register(CustomKernel as Filter<unknown>);
register(Liquify as Filter<unknown>);
register(Invert as Filter<unknown>);

export function getFilterById(id: string): Filter<unknown> | undefined {
  return registry.get(id);
}

export function getAllFilters(): Filter<unknown>[] {
  return Array.from(registry.values());
}

export function getFilterIds(): string[] {
  return Array.from(registry.keys());
}
